import type { Meta, StoryObj } from "@storybook/react-vite"

import UserBillsReceiptPage from "./bills/receipt"
import UserBillsShowPage from "./bills/show"
import UserDashboardsShowPage from "./dashboards/show"
import UserDirectoryIndexPage from "./directory/index"
import UserInvitesShowPage from "./invites/show"
import UserItemsShowPage from "./items/show"
import UserMenusShowPage from "./menus/show"
import UserProfilesEditPasswordPage from "./profiles/edit-password"
import UserProfilesEditPage from "./profiles/edit"
import UserProfilesShowPage from "./profiles/show"
import UserRegistrationsNewPage from "./registrations/new"
import UserSessionsNewPage from "./sessions/new"
import {
  createPageStory,
  sampleDinerPickup,
  sampleDirectoryResults,
  sampleDirectoryResultsNearby,
  sampleDinerSummary,
  sampleInvitePayload,
  sampleTimezones,
  sampleUserBill,
  sampleUserBillPickup,
  sampleUserItemDetail,
  sampleUserItemDetailEmoji,
  sampleUserMenuPayload,
  userPageProps,
} from "../storybook-helpers"

const meta = {
  title: "Pages/User",
  tags: ["autodocs"],
  parameters: {
    layout: "fullscreen",
  },
} satisfies Meta

export default meta

type Story = StoryObj<typeof meta>

export const BillsReceipt: Story = createPageStory(UserBillsReceiptPage, {
  name: "bills/receipt",
  url: "/bills/5001/receipt",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerSummary,
    // Settled, which is the state this screen exists to render: the letterhead
    // dates the payment and the footer names how it moved.
    bill: {
      ...sampleUserBill,
      status: "paid",
      paidAt: "2026-08-07T18:30:00Z",
      paymentMethods: [{ method: "digital_in_app", label: "Pago digital" }],
    },
  },
})

export const BillsShow: Story = createPageStory(UserBillsShowPage, {
  name: "bills/show",
  url: "/bills/5001",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerSummary,
    bill: sampleUserBill,
  },
})

export const BillsShowPickup: Story = createPageStory(UserBillsShowPage, {
  name: "bills/show (pickup)",
  url: "/bills/5202",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerPickup,
    bill: sampleUserBillPickup,
  },
})

export const DashboardsShow: Story = createPageStory(UserDashboardsShowPage, {
  name: "dashboards/show (inicio = directorio)",
  url: "/dashboard",
  pageProps: {
    ...userPageProps,
    diner: null,
    query: "",
    results: sampleDirectoryResults,
    nearby: false,
  },
})

export const DirectorySearch: Story = createPageStory(UserDirectoryIndexPage, {
  name: "directory/index (buscar restaurantes)",
  url: "/search",
  pageProps: {
    query: "",
    results: sampleDirectoryResults,
    nearby: false,
  },
})

export const DirectoryNearby: Story = createPageStory(UserDirectoryIndexPage, {
  name: "directory/index (ordenado por cercanía)",
  url: "/search",
  pageProps: {
    query: "",
    results: sampleDirectoryResultsNearby,
    nearby: true,
  },
})

export const InvitesShow: Story = createPageStory(UserInvitesShowPage, {
  name: "invites/show",
  url: "/invites/invite-storybook",
  pageProps: {
    ...userPageProps,
    invite: sampleInvitePayload,
  },
})

export const InvitesInvalid: Story = createPageStory(UserInvitesShowPage, {
  name: "invites/show (inválido)",
  url: "/invites/expired",
  pageProps: {
    ...userPageProps,
    error: "Esta invitación expiró o ya no está disponible.",
  },
})

export const ItemsShow: Story = createPageStory(UserItemsShowPage, {
  name: "items/show",
  url: "/items/101",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerSummary,
    item: sampleUserItemDetail,
  },
})

export const ItemsShowEmoji: Story = createPageStory(UserItemsShowPage, {
  name: "items/show (emoji, sin extras)",
  url: "/dinein/menu/items/103",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerSummary,
    item: sampleUserItemDetailEmoji,
  },
})

export const MenusShow: Story = createPageStory(UserMenusShowPage, {
  name: "menus/show",
  url: "/dinein/menu",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerSummary,
    menu: sampleUserMenuPayload,
  },
})

export const MenusShowPickup: Story = createPageStory(UserMenusShowPage, {
  name: "menus/show (pickup)",
  url: "/dinein/menu",
  pageProps: {
    ...userPageProps,
    diner: sampleDinerPickup,
    menu: sampleUserMenuPayload,
  },
})

export const ProfilesEditPassword: Story = createPageStory(UserProfilesEditPasswordPage, {
  name: "profiles/edit-password",
  url: "/profile/password",
  pageProps: userPageProps,
})

export const ProfilesEdit: Story = createPageStory(UserProfilesEditPage, {
  name: "profiles/edit",
  url: "/profile/edit",
  pageProps: userPageProps,
})

const profileSettingsProps = {
  timeZone: "America/Mexico_City",
  timezones: sampleTimezones,
}

export const ProfilesShow: Story = createPageStory(UserProfilesShowPage, {
  name: "profiles/show",
  url: "/profile",
  componentProps: profileSettingsProps,
  pageProps: userPageProps,
})

export const ProfilesShowGuest: Story = createPageStory(UserProfilesShowPage, {
  name: "profiles/show (invitado)",
  url: "/profile",
  componentProps: profileSettingsProps,
  pageProps: {
    ...userPageProps,
    user: { ...userPageProps.user, name: "Invitado 4821", anonymous: true },
  },
})

export const RegistrationsNew: Story = createPageStory(UserRegistrationsNewPage, {
  name: "registrations/new",
  url: "/registration/new",
  pageProps: {
    upgradeMode: false,
    step: "email",
    emailAddress: null,
    codeLength: 6,
    codeTtlMinutes: 10,
  },
})

export const RegistrationsUpgrade: Story = createPageStory(UserRegistrationsNewPage, {
  name: "registrations/new (upgrade invitado)",
  url: "/registration/new",
  pageProps: {
    upgradeMode: true,
  },
})

export const SessionsNew: Story = createPageStory(UserSessionsNewPage, {
  name: "sessions/new",
  url: "/session/new",
  pageProps: {
    flash: {
      alert: "Iniciá sesión o continuá como invitado.",
    },
  },
})
